import { Utils } from '@genial/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm, Control } from 'react-hook-form';

import {
  AssetDefaultOrSelectedTypeMap,
  SubmitUpdateProps,
} from '../../../../../../../store/product/types';

import { RedemptionFormData, validateFundRedemptionValueApplied } from './constants';

export const useFundsSellAssetController = (
  offer: AssetDefaultOrSelectedTypeMap['fundsSell'],
  submitUpdate?: (data: SubmitUpdateProps) => Promise<boolean>
) => {
  const { productId, amountToRedemption, minimumTransaction } = offer;
  const maxRedemptionValue = Number(amountToRedemption);

  const initialValue = offer.orderValue || maxRedemptionValue;
  const initialValueFormatted = Utils.Mask.Money(initialValue);
  const minRedemptionValue = minimumTransaction;

  const [isTotalRedemptionChecked, setIsTotalRedemptionChecked] = useState(false);
  const [redemptionValue, setRedemptionValue] = useState(initialValue);
  const [previousRedemptionValue, setPreviousRedemptionValue] = useState(initialValue);
  const [redemptionInputIsFocused, setRedemptionInputIsFocused] = useState(false);

  const redemptionForm = useForm<RedemptionFormData>({
    defaultValues: { redemptionValue: initialValueFormatted },
    mode: 'all',
    resolver: zodResolver(
      validateFundRedemptionValueApplied(minRedemptionValue, maxRedemptionValue)
    ),
    resetOptions: { keepValues: false },
    context: { minRedemptionValue, maxRedemptionValue },
  });

  const {
    formState: { errors },
    resetField,
    clearErrors,
    getValues,
    setValue,
  } = redemptionForm;

  const formattedRedemptionValue = Utils.Mask.Money(redemptionValue);

  const helpTextWhenIsFocused = `Mínimo: ${Utils.Mask.Money(minRedemptionValue)}`;
  const inputIsDisabled = isTotalRedemptionChecked;
  const fundRedemptionButtonIsDisabled = !!errors.redemptionValue;
  const handleInputFocus = () => {
    setRedemptionInputIsFocused(true);
    if (errors?.redemptionValue) {
      resetField('redemptionValue');
      setValue('redemptionValue', Utils.Mask.Money(previousRedemptionValue));
    }
  };

  const handleInputClear = () => {
    setRedemptionInputIsFocused(false);
    setPreviousRedemptionValue(maxRedemptionValue);
    resetField('redemptionValue');
    clearErrors('redemptionValue');
  };

  const handleInputBlur = () => {
    const currentRedemptionValue = getValues('redemptionValue');
    const currentRedemptionValueUnformatted = Utils.Unmask.Money(currentRedemptionValue);

    if (
      currentRedemptionValueUnformatted >= minRedemptionValue &&
      currentRedemptionValueUnformatted <= maxRedemptionValue
    ) {
      setValue('redemptionValue', currentRedemptionValue);
      setRedemptionValue(currentRedemptionValueUnformatted);
      setPreviousRedemptionValue(currentRedemptionValueUnformatted);
    } else {
      setValue('redemptionValue', Utils.Mask.Money(previousRedemptionValue));
      clearErrors('redemptionValue');
    }

    if (redemptionInputIsFocused) {
      setRedemptionInputIsFocused(false);
    }
  };

  const handleToggleIsTotalRedemption = (isActive: boolean) => {
    setIsTotalRedemptionChecked(isActive);
    if (isActive) {
      setRedemptionValue(maxRedemptionValue);
      setPreviousRedemptionValue(maxRedemptionValue);
      setValue('redemptionValue', Utils.Mask.Money(maxRedemptionValue));
    }
  };

  const handleSubmit = async ({ redemptionValue: redemptionValueForm }: RedemptionFormData) => {
    const updateFormValues = () => {
      const redemptionValueUnformatted = Utils.Unmask.Money(redemptionValueForm);
      redemptionForm.setValue('redemptionValue', redemptionValueForm);
      setValue('redemptionValue', redemptionValueForm);
      setRedemptionValue(redemptionValueUnformatted);
      setPreviousRedemptionValue(redemptionValueUnformatted);
    };

    if (submitUpdate) {
      const unformattedAmount = Utils.Unmask.Money(redemptionValueForm);
      const response = await submitUpdate({ amount: unformattedAmount });

      if (response) {
        updateFormValues();
      }
    } else {
      updateFormValues();
    }

    setRedemptionInputIsFocused(false);
  };

  return {
    productId,
    inputIsDisabled,
    helpText: helpTextWhenIsFocused,
    isFocused: redemptionInputIsFocused,
    handleInputFocus,
    handleInputClear,
    handleInputBlur,
    fundRedemptionButtonIsDisabled,
    handleToggleIsTotalRedemption,
    isTotalRedemption: isTotalRedemptionChecked || redemptionValue >= maxRedemptionValue,
    isTotalRedemptionChecked,
    currentValue: redemptionValue,
    currentValueFormatted: formattedRedemptionValue,
    handleSubmit: redemptionForm.handleSubmit(handleSubmit),
    control: redemptionForm.control as unknown as Control,
    hasError: redemptionForm.formState.errors?.redemptionValue,
  };
};

export type FundsSellAssetController = ReturnType<typeof useFundsSellAssetController>;
